import React, { ComponentType, ReactNode, useState } from 'react';
import { Loader2, LucideIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useExcelOperationLoadingSignal } from '@/lib/excel/operation-events';
import { cn } from '@/lib/utils';
export interface MenuItemProps {
  title: string;
  icon: ReactNode | LucideIcon;
  onClick?: () => void;
  group?: number;
  disabled?: boolean;
  loading?: boolean;
  loadingTitle?: string;
}

interface AritoMenuButtonProps {
  title?: string;
  items: MenuItemProps[];
  icon?: ReactNode | LucideIcon;
  className?: string;
}

const AritoMenuButton: React.FC<AritoMenuButtonProps> = ({ title, items, icon, className }) => {
  const [open, setOpen] = useState(false);
  const loadingExcelItem = items.find(
    item => item.loading && /excel|xuất|kết xuất/i.test(`${item.title} ${item.loadingTitle || ''}`)
  );

  useExcelOperationLoadingSignal(
    Boolean(loadingExcelItem),
    loadingExcelItem?.loadingTitle || 'Đang kết xuất dữ liệu...',
    `menu-button-${title || 'excel'}`
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const groupedItems: { [key: number]: MenuItemProps[] } = {};
  items.forEach(item => {
    const group = item.group || 0;
    if (!groupedItems[group]) {
      groupedItems[group] = [];
    }
    groupedItems[group].push(item);
  });

  const groupKeys = Object.keys(groupedItems).map(Number).sort();
  const menuItems = groupKeys.flatMap((group, groupIndex) => {
    const groupElements: ReactNode[] = [];

    if (groupIndex > 0) {
      groupElements.push(<DropdownMenuSeparator key={`divider-${group}`} className='my-1' />);
    }

    groupElements.push(
      ...groupedItems[group].map((item, itemIndex) => (
        <DropdownMenuItem
          key={`${group}-${itemIndex}`}
          disabled={item.disabled || item.loading}
          onClick={() => {
            if (item.disabled || item.loading) return;
            if (item.onClick) item.onClick();
            handleClose();
          }}
          className='min-h-8 cursor-pointer rounded-none px-2.5 py-2 text-[13px] hover:bg-[#dcebfc] focus:bg-[#dcebfc]'
        >
          {item.loading ? (
            <Loader2 className='size-4 animate-spin text-[#023e8a]' />
          ) : React.isValidElement(item.icon) ? (
            item.icon
          ) : (
            React.createElement(item.icon as ComponentType<{ className?: string }>, {
              className: 'text-[#023e8a] size-4'
            })
          )}
          <span className='text-gray-900'>{item.loading && item.loadingTitle ? item.loadingTitle : item.title}</span>
        </DropdownMenuItem>
      ))
    );

    return groupElements;
  });

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          onClick={handleClick}
          className={cn(
            `flex flex-col items-center justify-center rounded-[2.5px] bg-main px-3 py-1 font-semibold text-white transition duration-75 hover:bg-main-dark hover:shadow-sm`,
            className
          )}
        >
          {icon ? (
            React.isValidElement(icon) ? (
              icon
            ) : (
              React.createElement(icon as ComponentType<{ className?: string }>, { className: 'text-white size-4' })
            )
          ) : (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='4' y1='6' x2='20' y2='6'></line>
              <line x1='4' y1='12' x2='20' y2='12'></line>
              <line x1='4' y1='18' x2='20' y2='18'></line>
            </svg>
          )}

          <span className='text-xs'>{title}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='max-w-[420px] rounded-none border-[#d0d0d0] p-[3px_0] shadow-[0_2px_10px_rgba(0,0,0,0.1)]'
      >
        {menuItems}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AritoMenuButton;
